/**
 * Eric 2026-08-05 — Direct browser → OVH S3 multipart upload (client-side).
 *
 * Utilisation :
 *
 *   const result = await uploadDirect(file, {
 *     API, csrfHeaders,
 *     title, description, theme,
 *     firefliesFile,       // optionnel
 *     diarizationMode,     // "single" | "multi_select" | null
 *     singleSpeakerId,
 *     multiSelectedSpeakerIds,  // Set<string>
 *     concurrency: 3,      // parts en parallèle
 *   }, {
 *     onProgress: ({ completed, total, bytesUploaded }) => {...},
 *     onStage: (stage) => {...},      // "init" | "upload" | "complete"
 *     onSlowUpload: () => {...},      // callback à 10 min elapsed
 *     signal,                          // AbortController.signal pour cancel
 *   });
 *
 * Retour : { id, storage_path, file_size, _uploadReport }
 *   _uploadReport a la même forme que l'ancien flow chunké (mais mode="direct_s3")
 */
import axios from "axios";

const SLOW_UPLOAD_THRESHOLD_MS = 10 * 60 * 1000;

async function _putPart(url, blob, partNumber, maxRetries = 5) {
  /** PUT une part à OVH avec retry exponentiel. Retourne { PartNumber, ETag,
   *  attempts, elapsed_ms }. Chaque échec est loggé. */
  const attempts = [];
  let lastErr;
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    const t0 = Date.now();
    try {
      const resp = await fetch(url, {
        method: "PUT",
        body: blob,
        // Volontairement PAS de Content-Type header : la signature S3 v4
        // pour upload_part n'inclut PAS le Content-Type, alors si on l'ajoute
        // ici le navigateur mettra "application/octet-stream" et signature
        // mismatch. On laisse le navigateur ne rien envoyer.
      });
      if (!resp.ok) {
        const body = await resp.text().catch(() => "");
        throw new Error(`HTTP ${resp.status}: ${body.slice(0, 200)}`);
      }
      const etag = resp.headers.get("ETag") || resp.headers.get("etag");
      if (!etag) {
        throw new Error("ETag manquant dans la réponse S3");
      }
      return {
        PartNumber: partNumber,
        ETag: etag,
        attempts: attempt,
        elapsed_ms: Date.now() - t0,
        failures: attempts,
      };
    } catch (err) {
      lastErr = err;
      attempts.push({
        attempt,
        error: (err && err.message) || String(err),
        at: new Date().toISOString(),
      });
      if (attempt < maxRetries) {
        // Backoff exponentiel : 500 ms, 1 s, 2 s, 4 s...
        await new Promise((r) => setTimeout(r, 500 * Math.pow(2, attempt - 1)));
      }
    }
  }
  const err = new Error(`Part ${partNumber} échec après ${maxRetries} tentatives: ${lastErr?.message || lastErr}`);
  err.failures = attempts;
  err.partNumber = partNumber;
  throw err;
}


export async function uploadDirect(file, params, callbacks = {}) {
  const {
    API,
    title, description, theme,
    firefliesFile,
    diarizationMode, singleSpeakerId, multiSelectedSpeakerIds,
    concurrency = 3,
  } = params;
  const { onProgress, onStage, onSlowUpload, signal } = callbacks;

  const startedAt = Date.now();
  onStage?.("init");

  // ── 1) INIT : demande au backend une session S3 multipart + URLs par part
  const initStart = Date.now();
  const initReq = {
    filename: file.name,
    total_size: file.size,
    content_type: file.type || "video/mp4",
    title, description,
    training_theme: theme,
    diarization_mode: diarizationMode,
    single_speaker_id: singleSpeakerId || null,
    multi_selected_speaker_ids: multiSelectedSpeakerIds
      ? Array.from(multiSelectedSpeakerIds)
      : [],
  };
  const { data: initResp } = await axios.post(
    `${API}/api/videos/upload/direct/init`,
    initReq,
    { withCredentials: true },
  );
  const { video_id, key, part_size, total_parts, presigned_urls } = initResp;
  const initMs = Date.now() - initStart;

  // ── 2) OPTIONNEL : Upload Fireflies direct-S3 (single PUT presigned)
  //
  // Vincent B749 v3 (Eric 2026-02-18 SPEC STRICK PROD) — Le PUT S3 était
  // silencieux (`await fetch(...)` sans `.ok` check). Si S3 rejette (403
  // presigned expiré, CORS bloqué, ContentType mismatch, 5xx transient),
  // le fetch se résout SANS erreur, `firefliesKey` était renvoyé au backend
  // qui posait `fireflies_storage_path` sur video_doc, puis le
  // `direct/complete` tentait `get_object(fireflies_key)` → 404 →
  // `except Exception` swallowait tout → aucune trace, aucun retry.
  //
  // Fix triple :
  //   1) `response.ok` check → throw immédiat si S3 rejette (visible dans le toast d'erreur).
  //   2) 2 retries automatiques avec 1s de délai (tolérer les 5xx transient).
  //   3) Log console explicite en cas d'échec pour diagnostic.
  let firefliesKey = null;
  if (firefliesFile) {
    const { data: ffResp } = await axios.post(
      `${API}/api/videos/upload/direct/fireflies-url`,
      { video_id, filename: firefliesFile.name },
      { withCredentials: true },
    );
    firefliesKey = ffResp.key;
    // PUT direct to S3 avec retry x3 (1s délai entre tentatives).
    let _ffLastErr = null;
    let _ffOk = false;
    for (let _attempt = 1; _attempt <= 3; _attempt += 1) {
      try {
        const _resp = await fetch(ffResp.url, {
          method: "PUT",
          body: firefliesFile,
          headers: { "Content-Type": "application/octet-stream" },
        });
        if (!_resp.ok) {
          const _body = await _resp.text().catch(() => "");
          throw new Error(`S3 PUT fireflies HTTP ${_resp.status}: ${_body.slice(0, 300)}`);
        }
        _ffOk = true;
        break;
      } catch (err) {
        _ffLastErr = err;
        console.warn(`[uploadDirect fireflies] tentative ${_attempt}/3 échouée:`, err?.message || err);
        if (_attempt < 3) {
          await new Promise((r) => setTimeout(r, 1000));
        }
      }
    }
    if (!_ffOk) {
      // Le fichier vidéo est déjà en cours d'upload — on ne veut pas TOUT
      // annuler pour un Fireflies raté. On invalide juste `firefliesKey`
      // pour que le backend ne pose pas `fireflies_storage_path` sur un
      // fichier fantôme, et on remonte l'erreur au caller (toast).
      firefliesKey = null;
      console.error("[uploadDirect fireflies] Échec final après 3 tentatives:", _ffLastErr);
      throw new Error(
        `Upload Fireflies vers S3 échoué : ${_ffLastErr?.message || "inconnu"}. `
        + "Vérifie CORS S3 ou réessaie l'upload.",
      );
    }
  }

  // ── 3) UPLOAD PARTS EN PARALLÈLE avec worker pool + timer alerte lente
  onStage?.("upload");
  const partsUploadStart = Date.now();
  let bytesUploaded = 0;
  let completedParts = 0;
  const partFailureLog = [];
  const results = new Array(total_parts);

  // Timer 10 min → alerte lente (best-effort, ne bloque pas l'upload)
  let slowWarned = false;
  const slowTimer = setTimeout(() => {
    if (slowWarned) return;
    slowWarned = true;
    onSlowUpload?.();
    // La feature « alerte 10 min » côté serveur est branchée sur le upload_id
    // du flow chunké. Pour le direct upload, on utilise le video_id comme
    // clé d'idempotence — on informe l'admin via post-mortem au /complete
    // uniquement (via PATCH upload-report). Un endpoint realtime dédié
    // sera ajouté si on veut aussi une alerte pendant l'upload direct.
  }, SLOW_UPLOAD_THRESHOLD_MS);

  const uploadOnePart = async (partMeta) => {
    if (signal?.aborted) throw new Error("Upload annulé par l'utilisateur");
    const pn = partMeta.part_number;
    const start = (pn - 1) * part_size;
    const end = Math.min(start + part_size, file.size);
    const blob = file.slice(start, end);
    const partResult = await _putPart(partMeta.url, blob, pn);
    for (const f of partResult.failures || []) {
      partFailureLog.push({ part_number: pn, ...f });
    }
    results[pn - 1] = { PartNumber: partResult.PartNumber, ETag: partResult.ETag };
    completedParts += 1;
    bytesUploaded += (end - start);
    onProgress?.({ completed: completedParts, total: total_parts, bytesUploaded });
  };

  // Worker pool : concurrency parts en parallèle
  const queue = [...presigned_urls];
  const workers = Array.from({ length: Math.min(concurrency, queue.length) }, async () => {
    while (queue.length > 0) {
      const next = queue.shift();
      if (!next) break;
      await uploadOnePart(next);
    }
  });
  try {
    await Promise.all(workers);
  } catch (err) {
    clearTimeout(slowTimer);
    // Tentative d'abort côté serveur (best-effort)
    axios.post(
      `${API}/api/videos/upload/direct/abort`,
      { video_id },
      { withCredentials: true, timeout: 10000 },
    ).catch(() => {});
    throw err;
  }
  const partsMs = Date.now() - partsUploadStart;

  // ── 4) COMPLETE : finalise le multipart sur S3 + crée le doc vidéo
  onStage?.("complete");
  const completeStart = Date.now();
  const { data: completeResp } = await axios.post(
    `${API}/api/videos/upload/direct/complete`,
    { video_id, parts: results },
    { withCredentials: true, timeout: 120000 },
  );
  const completeMs = Date.now() - completeStart;
  clearTimeout(slowTimer);

  // ── Compile le rapport d'envoi (même shape que le flow chunké,
  // mais mode="direct_s3" et concurrence par part)
  const totalMs = Date.now() - startedAt;
  const failuresByType = {};
  for (const f of partFailureLog) {
    const key = /HTTP (\d+)/.exec(f.error) ? `HTTP ${/HTTP (\d+)/.exec(f.error)[1]}` : "erreur réseau";
    failuresByType[key] = (failuresByType[key] || 0) + 1;
  }
  const partsWithRetries = new Set(partFailureLog.map((f) => f.part_number)).size;

  return {
    id: completeResp.id,
    storage_path: completeResp.storage_path,
    file_size: completeResp.file_size,
    _uploadReport: {
      mode: "direct_s3",
      total_ms: totalMs,
      init_ms: initMs,
      chunks_ms: partsMs,
      complete_ms: completeMs,
      total_chunks: total_parts,       // renommé côté serveur "parts"
      already_received: 0,             // pas de resume S3 côté client dans cette v1
      resumed: false,
      file_size: file.size,
      concurrency,
      chunks_with_retries: partsWithRetries,
      total_retries: partFailureLog.length,
      failures_by_type: failuresByType,
      s3_key: key,
      fireflies_key: firefliesKey,
      // Vincent B837 v2 (Eric 2026-02-14 SPEC STRICK PROD) — Manquait
      // ici (déjà présent dans les 2 autres modes). Vincent a signalé
      // que le rapport post-upload n'affichait rien : c'est bien le
      // mode `direct_s3` qui est déclenché par défaut, pas `chunked`.
      video_filename: file?.name || null,
      video_id: completeResp?.id || null,
      fireflies_filename: firefliesFile?.name || null,
      fireflies_id: completeResp?.fireflies_id || completeResp?.fireflies_transcript_id || null,
    },
  };
}
